import React, { useCallback, useMemo, useState } from 'react';
import Button from './Button';
import { useError } from '../contexts/ErrorContext';
import { createFeatureSegment, deleteFeatureSegment } from '../services/datasetsService';
import './FeatureSegmentationPanel.css';

const pad2 = (n) => String(n).padStart(2, '0');

const formatHmsFromEpoch = (timestampSeconds) => {
  if (timestampSeconds == null || Number.isNaN(Number(timestampSeconds))) return '--:--:--';
  const date = new Date(Number(timestampSeconds) * 1000);
  // 24-hour HH:MM:SS
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

const parseHms = (value) => {
  if (!value || typeof value !== 'string') return null;
  const parts = value.split(':').map((p) => p.trim());
  if (parts.length < 2 || parts.length > 3) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  const s = parts.length === 3 ? Number(parts[2]) : 0;
  if ([h, m, s].some((v) => Number.isNaN(v))) return null;
  if (h < 0 || h > 23) return null;
  if (m < 0 || m > 59) return null;
  if (s < 0 || s > 59) return null;
  return { h, m, s };
};

const toPointFeature = (loc, featureType, segmentId) => {
  const props = {
    timestamp: loc.timestamp,
    feature_type: featureType, // required: "run" | "lift"
    segment_id: segmentId || null,
  };

  // Include telemetry fields if present
  if (loc.altitude !== void 0) props.altitude = loc.altitude;
  if (loc.speed !== void 0) props.speed = loc.speed;
  if (loc.course !== void 0) props.course = loc.course;
  if (loc.accuracy !== void 0) props.accuracy = loc.accuracy;
  if (loc.verticalAccuracy !== void 0) props.verticalAccuracy = loc.verticalAccuracy;
  if (loc.speedAccuracy !== void 0) props.speedAccuracy = loc.speedAccuracy;
  if (loc.sourceFile !== void 0) props.sourceFile = loc.sourceFile;
  if (loc.stats !== void 0) props.stats = loc.stats;

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [loc.lon, loc.lat],
    },
    properties: props,
  };
};

/**
 * Feature segmentation panel
 * Allows users to create labeled intervals (start, end, feature type).
 *
 * Times are in dataset timestamp seconds (same as `currentTime` used by Timeline).
 */
const FeatureSegmentationPanel = ({
  datasetId,
  datasetName,
  locations,
  currentTime,
  referenceEpochSeconds,
  segments,
  onSegmentsChange,
  visualizedSegmentId,
  onToggleVisualize
}) => {
  const { addError } = useError();

  // User-facing inputs are time-of-day strings: "HH:MM:SS" (seconds optional depending on browser)
  const [startHms, setStartHms] = useState('');
  const [endHms, setEndHms] = useState('');
  const [featureType, setFeatureType] = useState('run'); // 'run' | 'lift'
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const canSetFromCurrent = currentTime != null;

  const epochFromHmsOnReferenceDay = useCallback((hmsValue) => {
    const parsed = parseHms(hmsValue);
    if (!parsed) return null;

    const ref = referenceEpochSeconds ?? currentTime;
    if (ref == null || Number.isNaN(Number(ref))) return null;

    const d = new Date(Number(ref) * 1000);
    d.setHours(parsed.h, parsed.m, parsed.s, 0);
    return Math.floor(d.getTime() / 1000);
  }, [referenceEpochSeconds, currentTime]);

  const startPreview = useMemo(() => {
    const epoch = epochFromHmsOnReferenceDay(startHms);
    return formatHmsFromEpoch(epoch);
  }, [startHms, epochFromHmsOnReferenceDay]);

  const endPreview = useMemo(() => {
    const epoch = epochFromHmsOnReferenceDay(endHms);
    return formatHmsFromEpoch(epoch);
  }, [endHms, epochFromHmsOnReferenceDay]);

  const handleSetStart = () => {
    if (!canSetFromCurrent) return;
    setStartHms(formatHmsFromEpoch(currentTime));
  };

  const handleSetEnd = () => {
    if (!canSetFromCurrent) return;
    setEndHms(formatHmsFromEpoch(currentTime));
  };

  const handleAdd = async () => {
    const s = epochFromHmsOnReferenceDay(startHms);
    const e = epochFromHmsOnReferenceDay(endHms);

    if (s == null) {
      addError('Start time is required (HH:MM:SS)', null, 'Feature segmentation');
      return;
    }
    if (e == null) {
      addError('End time is required (HH:MM:SS)', null, 'Feature segmentation');
      return;
    }
    if (e < s) {
      addError('End time must be >= start time', null, 'Feature segmentation');
      return;
    }
    if (featureType !== 'run' && featureType !== 'lift') {
      addError('Feature type must be "run" or "lift"', null, 'Feature segmentation');
      return;
    }

    if (!datasetId) {
      addError('Missing dataset id; cannot save segment', null, 'Feature segmentation');
      return;
    }

    try {
      setSaving(true);
      const created = await createFeatureSegment(
        datasetId,
        { startTime: s, endTime: e, featureType },
        addError
      );
      onSegmentsChange((prev) => [...prev, created]);
    } catch (error) {
      // createFeatureSegment already streams to ErrorContext
      console.error('Failed to save feature segment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (!locations || locations.length === 0) {
      addError('No telemetry locations loaded', null, 'Feature segmentation export');
      return;
    }
    if (!segments || segments.length === 0) {
      addError('Nothing to export: no labeled segments', null, 'Feature segmentation export');
      return;
    }

    const features = [];
    for (const seg of segments) {
      const segType = seg.featureType === 'lift' ? 'lift' : 'run';
      const segStart = Number(seg.startTime);
      const segEnd = Number(seg.endTime);
      if (!Number.isFinite(segStart) || !Number.isFinite(segEnd)) continue;

      const pointsInSeg = locations.filter(
        (loc) => loc && Number.isFinite(Number(loc.timestamp)) && loc.timestamp >= segStart && loc.timestamp <= segEnd
      );

      for (const loc of pointsInSeg) {
        if (loc?.lat == null || loc?.lon == null) continue;
        features.push(toPointFeature(loc, segType, seg.id || null));
      }
    }

    if (features.length === 0) {
      addError('Nothing to export: no telemetry points inside labeled segments', null, 'Feature segmentation export');
      return;
    }

    const geojson = {
      type: 'FeatureCollection',
      features,
    };

    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/geo+json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const safeName = String(datasetName || datasetId || 'dataset')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    a.download = `${safeName}_feature_segmentation.geojson`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (segmentId) => {
    if (!datasetId || !segmentId) return;
    try {
      setDeletingId(segmentId);
      await deleteFeatureSegment(datasetId, segmentId, addError);
      onSegmentsChange((prev) => prev.filter((s) => s && s.id !== segmentId));
      if (visualizedSegmentId === segmentId && onToggleVisualize) {
        onToggleVisualize(null);
      }
    } catch (error) {
      // deleteFeatureSegment already streams to ErrorContext
      console.error('Failed to delete feature segment:', error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="feature-segmentation-panel">
      <div className="feature-segmentation-header">
        <h2>Feature segmentation</h2>
        <div className="feature-segmentation-header-actions">
          <div className="feature-segmentation-chip">
            <>
              {`${segments.length} segment${segments.length === 1 ? '' : 's'}`}
              {visualizedSegmentId ? ' • Viewing 1' : ''}
            </>
          </div>
          <Button
            variant="outline"
            size="small"
            onClick={handleExport}
            disabled={segments.length === 0}
          >
            Export GeoJSON
          </Button>
        </div>
      </div>

      <div className="feature-segmentation-form">
        <div className="feature-segmentation-row">
          <div className="feature-segmentation-field">
            <div className="feature-segmentation-label">Start time (HH:MM:SS)</div>
            <input
              className="feature-segmentation-input"
              type="time"
              step="1"
              value={startHms}
              onChange={(e) => setStartHms(e.target.value)}
            />
            <div className="feature-segmentation-label">Start: {startPreview}</div>
          </div>
          <Button
            variant="outline"
            size="small"
            onClick={handleSetStart}
            disabled={!canSetFromCurrent}
          >
            Set start
          </Button>
        </div>

        <div className="feature-segmentation-row">
          <div className="feature-segmentation-field">
            <div className="feature-segmentation-label">End time (HH:MM:SS)</div>
            <input
              className="feature-segmentation-input"
              type="time"
              step="1"
              value={endHms}
              onChange={(e) => setEndHms(e.target.value)}
            />
            <div className="feature-segmentation-label">End: {endPreview}</div>
          </div>
          <Button
            variant="outline"
            size="small"
            onClick={handleSetEnd}
            disabled={!canSetFromCurrent}
          >
            Set end
          </Button>
        </div>

        <div className="feature-segmentation-row">
          <div className="feature-segmentation-field">
            <div className="feature-segmentation-label">Feature</div>
            <select
              className="feature-segmentation-select"
              value={featureType}
              onChange={(e) => setFeatureType(e.target.value)}
            >
              <option value="lift">lift</option>
              <option value="run">run</option>
            </select>
          </div>
          <div className="feature-segmentation-actions">
            <Button variant="primary" size="small" onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : 'Add'}
            </Button>
          </div>
        </div>
      </div>

      <div className="feature-segmentation-list">
        <div className="feature-segmentation-list-header">Start time | End time | Feature type</div>
        {segments.length === 0 ? (
          <div className="feature-segmentation-empty">No segments yet.</div>
        ) : (
          segments.map((seg, idx) => (
            (() => {
              const isSelected = visualizedSegmentId === seg.id;
              return (
            <div
              key={`${seg.startTime}-${seg.endTime}-${idx}`}
              className={`feature-segmentation-item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                if (!onToggleVisualize) return;
                onToggleVisualize(seg);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!onToggleVisualize) return;
                  onToggleVisualize(seg);
                }
              }}
              aria-label={`Toggle segment visualization: ${formatHmsFromEpoch(seg.startTime)} to ${formatHmsFromEpoch(seg.endTime)} (${seg.featureType})`}
            >
              <div className="feature-segmentation-item-time">{formatHmsFromEpoch(seg.startTime)}</div>
              <div className="feature-segmentation-item-time">{formatHmsFromEpoch(seg.endTime)}</div>
              <div className="feature-segmentation-item-actions">
                <div className={`feature-segmentation-item-type ${seg.featureType === 'lift' ? 'lift' : 'run'}`}>
                  {seg.featureType === 'lift' ? 'lift' : 'run'}
                </div>
                {isSelected && (
                  <Button
                    variant="danger"
                    size="small"
                    disabled={deletingId === seg.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(seg.id);
                    }}
                    aria-label="Delete segment"
                  >
                    {deletingId === seg.id ? 'Deleting…' : 'Remove'}
                  </Button>
                )}
              </div>
            </div>
              );
            })()
          ))
        )}
      </div>
    </div>
  );
};

export default FeatureSegmentationPanel;


